import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "../../lib/utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        "duration-100 ease-out-fast",
        className,
      )}
      {...props}
    />
  );
}

type SheetSide = "top" | "right" | "bottom" | "left";
type SheetSize = "sm" | "md" | "lg" | "full";

// Arbitrary-value widths (rather than `w-pane`) so tailwind-merge recognises
// them as the `w-*` group and a caller-supplied width still wins.
const sheetSizes: Record<SheetSize, string> = {
  sm: "w-96",
  md: "w-pane",
  lg: "w-pane-lg",
  full: "w-full",
};

export type SheetContentProps = React.ComponentProps<
  typeof SheetPrimitive.Content
> & {
  side?: SheetSide;
  size?: SheetSize;
  hideClose?: boolean;
};

function SheetContent({
  className,
  children,
  side = "right",
  size = "md",
  hideClose = false,
  ...props
}: SheetContentProps) {
  const horizontal = side === "right" || side === "left";

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "fixed z-50 flex flex-col bg-elevated shadow-overlay outline-none",
          "duration-180 ease-out-fast",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          horizontal && "inset-y-0 h-full max-w-full",
          horizontal && sheetSizes[size],
          // Radius on the inboard edges only: the sheet is flush with the
          // viewport on the side it slides in from, and a radius on an edge
          // that has nothing behind it reads as a rendering artefact.
          side === "right" &&
            "right-0 rounded-l-xl data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
          side === "left" &&
            "left-0 rounded-r-xl data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
          side === "top" &&
            "inset-x-0 top-0 h-auto rounded-b-xl data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top",
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto rounded-t-xl data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <SheetPrimitive.Close
            aria-label="Close"
            className={cn(
              "absolute right-4 top-4 grid size-6 place-items-center rounded-sm",
              "text-text-tertiary hover:bg-hover hover:text-text",
              "transition-[background-color,color] duration-100 ease-out-fast",
              "focus-visible:outline-none focus-fill",
              "disabled:pointer-events-none",
            )}
          >
            <XIcon className="size-3.5" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex h-header shrink-0 items-center gap-2 px-6",
        className,
      )}
      {...props}
    />
  );
}

function SheetBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      // `bg-sunken`, not the `bg-panel` a page footer bar gets: the sheet is
      // already the overlay tier, and a raised surface inside an overlay reads
      // backwards in dark (panel is darker than elevated). Sunken is
      // translucent, so it recedes on either tier and still occludes nothing.
      className={cn(
        "mt-auto flex h-header shrink-0 items-center justify-end gap-2 bg-sunken px-6",
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-sm font-semibold text-text", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-xs text-text-secondary", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
